if (!customElements.get("sticky-add-to-cart")) {
  customElements.define(
    "sticky-add-to-cart",
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.onScroll = this.onScroll.bind(this);
        this.onCtaClick = this.onCtaClick.bind(this);
        this.onMainFormSubmit = this.onMainFormSubmit.bind(this);
        this.onProductChange = this.onProductChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productSection = document.getElementById(
          `MainProduct-${this.sectionId}`,
        );
        if (!this.productSection) return;

        this.productForm = document.getElementById(
          `product-form-${this.sectionId}`,
        );
        this.sizeGroup = this.findSizeGroup();
        this.button = this.querySelector(".sticky-add-to-cart__button");
        this.buttonLabel = this.button?.querySelector("span");
        this.hasSizeOption = this.dataset.hasSizeOption === "true";
        this.selectSizeLabel =
          this.dataset.selectSizeLabel ||
          window.variantStrings?.selectSize ||
          "Select a size";
        this.addToCartLabel =
          this.dataset.addToCartLabel ||
          window.variantStrings?.addToCart ||
          "Add to cart";
        this.soldOutLabel =
          this.dataset.soldOutLabel ||
          window.variantStrings?.soldOut ||
          "Sold out";
        this.selectFirstAvailableVariant =
          this.dataset.selectFirstAvailableVariant === "true";
        this.sizeSelected =
          !this.hasSizeOption || this.selectFirstAvailableVariant;

        if (!this.productForm || !this.button) return;

        this.captureInitialAddButtonState();

        this.hidden = false;
        this.button.addEventListener("click", this.onCtaClick);
        this.productForm.addEventListener(
          "submit",
          this.onMainFormSubmit,
          true,
        );
        this.productSection.addEventListener("change", this.onProductChange);
        this.productSection.addEventListener("click", this.onProductChange);
        window.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onScroll);

        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange,
          );
        }

        this.updateCtaState();
        this.onScroll();
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.onCtaClick);
        this.productForm?.removeEventListener(
          "submit",
          this.onMainFormSubmit,
          true,
        );
        this.productSection?.removeEventListener(
          "change",
          this.onProductChange,
        );
        this.productSection?.removeEventListener("click", this.onProductChange);
        window.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("resize", this.onScroll);

        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      findSizeGroup() {
        const explicitSizeGroup = this.productSection.querySelector(
          ".product-form__input.size",
        );
        if (explicitSizeGroup) return explicitSizeGroup;

        const sizeInput = [
          ...this.productSection.querySelectorAll(
            'input[type="radio"], select',
          ),
        ].find((input) => {
          const name = input.getAttribute("name")?.toLowerCase();
          return name === "size" || name === "options[size]";
        });
        return sizeInput?.closest(".product-form__input") || null;
      }

      captureInitialAddButtonState() {
        const addButton = this.getAddButton();
        this.initialAddButtonState = {
          disabled: Boolean(addButton?.disabled),
          label:
            addButton?.querySelector("span")?.textContent?.trim() ||
            this.addToCartLabel,
        };
      }

      onProductChange(event) {
        const target = event.target;
        if (!target) return;

        const label = target.closest?.("label");
        const labelFor = label?.getAttribute("for");
        const escapedLabelFor =
          labelFor && window.CSS?.escape ? CSS.escape(labelFor) : labelFor;
        const clickedSizeInput =
          target.matches?.('input[type="radio"], select') &&
          this.sizeGroup?.contains(target);
        const clickedSizeLabel =
          label &&
          this.sizeGroup?.contains(label) &&
          escapedLabelFor &&
          this.sizeGroup?.querySelector(`#${escapedLabelFor}`);
        const sizeTarget = clickedSizeInput || clickedSizeLabel;
        if (this.hasSizeOption && sizeTarget) {
          const sizeInput = clickedSizeInput
            ? target
            : this.sizeGroup?.querySelector(`#${escapedLabelFor}`);
          this.sizeSelected = true;
          this.restoreAddButtonState(sizeInput?.classList.contains("disabled"));
          this.updateCtaState();
        }

        if (target.matches?.('input[type="radio"], select')) {
          this.updateStickyOptionText(target);
          this.updateCtaStateAfterOptionChange();
        }
      }

      onVariantChange(event) {
        if (event.data.sectionId !== this.sectionId) return;

        const source = event.data.html.getElementById(
          `StickyAddToCart-${this.sectionId}`,
        );
        if (!source) return;

        const sourcePrice = source.querySelector(
          `#StickyAddToCartPrice-${this.sectionId}`,
        );
        const destinationPrice = this.querySelector(
          `#StickyAddToCartPrice-${this.sectionId}`,
        );
        if (sourcePrice && destinationPrice)
          destinationPrice.innerHTML = sourcePrice.innerHTML;

        const sourceColor = source.querySelector(".sticky-add-to-cart__color");
        const destinationColor = this.querySelector(
          ".sticky-add-to-cart__color",
        );
        if (sourceColor && destinationColor)
          destinationColor.textContent = sourceColor.textContent;

        const sourceImage = source.querySelector(".sticky-add-to-cart__image");
        const destinationImage = this.querySelector(
          ".sticky-add-to-cart__image",
        );
        if (sourceImage && destinationImage) {
          destinationImage.src = sourceImage.src;
          destinationImage.alt = sourceImage.alt;
        }

        this.updateCtaState();
      }

      onScroll() {
        const addButton = this.getAddButton();
        if (!addButton) return;

        const addButtonHasPassed =
          addButton.getBoundingClientRect().bottom <= 0;
        this.classList.toggle("is-visible", addButtonHasPassed);
      }

      onCtaClick() {
        const addButton = this.getAddButton();

        if (this.hasSizeOption && !this.sizeSelected) {
          this.scrollToSizeGrid();
          return;
        }

        if (addButton && !addButton.disabled) {
          addButton.click();
          return;
        }

        this.scrollToSizeGrid();
      }

      onMainFormSubmit(event) {
        const addButton = this.getAddButton();
        if (addButton?.dataset.stickyNeedsSize !== "true") return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.triggerSelectSizeFeedback(addButton);
      }

      updateCtaState() {
        const addButton = this.getAddButton();
        const needsSize = this.hasSizeOption && !this.sizeSelected;
        this.syncMainAddButtonNeedsSizeState(needsSize);

        const addButtonText = addButton
          ?.querySelector("span")
          ?.textContent?.trim();
        const isStickyNeedsSize = addButton?.dataset.stickyNeedsSize === "true";
        const label =
          addButton?.disabled && !isStickyNeedsSize
            ? addButtonText || this.soldOutLabel
            : needsSize
              ? this.selectSizeLabel
              : addButton?.disabled
                ? addButtonText || this.soldOutLabel
                : this.addToCartLabel;

        if (this.buttonLabel) this.buttonLabel.textContent = label;
        this.button.setAttribute("aria-label", label);
        this.button.disabled =
          Boolean(addButton?.disabled) && !isStickyNeedsSize;
      }

      updateStickyOptionText(target) {
        const optionName = target.name?.toLowerCase();
        if (!optionName || (optionName !== "color" && optionName !== "colour"))
          return;

        const color = this.querySelector(".sticky-add-to-cart__color");
        if (color) color.textContent = target.value;
      }

      updateCtaStateAfterOptionChange() {
        window.requestAnimationFrame(() => this.updateCtaState());
      }

      getAddButton() {
        return (
          this.productForm?.querySelector(
            `#ProductSubmitButton-${this.sectionId}`,
          ) || this.productForm?.querySelector('[name="add"]')
        );
      }

      setAddButtonLabel(label) {
        const addButtonLabel = this.getAddButton()?.querySelector("span");
        if (addButtonLabel) addButtonLabel.textContent = label;
      }

      syncMainAddButtonNeedsSizeState(needsSize) {
        const addButton = this.getAddButton();
        if (!addButton) return;

        if (needsSize && !this.initialAddButtonState.disabled) {
          addButton.removeAttribute("disabled");
          addButton.dataset.stickyNeedsSize = "true";
          this.setAddButtonLabel(this.selectSizeLabel);
        }
      }

      restoreAddButtonState(sizeIsUnavailable) {
        const addButton = this.getAddButton();
        if (!addButton || addButton.dataset.stickyNeedsSize !== "true") return;

        delete addButton.dataset.stickyNeedsSize;

        if (sizeIsUnavailable || this.initialAddButtonState.disabled) {
          addButton.setAttribute("disabled", "disabled");
          this.setAddButtonLabel(
            sizeIsUnavailable
              ? this.soldOutLabel
              : this.initialAddButtonState.label,
          );
          return;
        }

        addButton.removeAttribute("disabled");
        this.setAddButtonLabel(this.addToCartLabel);
      }

      triggerSelectSizeFeedback(button) {
        if (button) {
          button.classList.remove(
            "product-form__submit--wiggle",
            "sticky-add-to-cart__button--wiggle",
          );
          void button.offsetWidth;
          button.classList.add(
            button.classList.contains("sticky-add-to-cart__button")
              ? "sticky-add-to-cart__button--wiggle"
              : "product-form__submit--wiggle",
          );

          window.setTimeout(() => {
            button.classList.remove(
              "product-form__submit--wiggle",
              "sticky-add-to-cart__button--wiggle",
            );
          }, 600);
        }
      }

      scrollToSizeGrid() {
        const target =
          this.sizeGroup || this.productForm || this.productSection;
        const offset = this.getBoundingClientRect().height + 24;
        const targetY =
          target.getBoundingClientRect().top + window.pageYOffset - offset;

        this.animateScrollTo(Math.max(targetY, 0), 850);
      }

      animateScrollTo(targetY, duration) {
        const startY = window.pageYOffset;
        const distance = targetY - startY;
        const startTime = performance.now();

        const step = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress =
            progress < 0.5
              ? 4 * progress * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 3) / 2;

          window.scrollTo(0, startY + distance * easedProgress);

          if (progress < 1) window.requestAnimationFrame(step);
        };

        window.requestAnimationFrame(step);
      }
    },
  );
}
